import { Head } from "@inertiajs/react";
import { useMemo, useState } from "react";

import WishlistGrid from "@/theme2/components/wishlist/WishlistGrid";
import WishlistHeader from "@/theme2/components/wishlist/WishlistHeader";
import WishlistToolbar from "@/theme2/components/wishlist/WishlistToolbar";
import WishlistSummary from "@/theme2/components/wishlist/WishlistSummary";
import EmptyWishlist from "@/theme2/components/wishlist/EmptyWishlist";
import WishlistPagination from "@/theme2/components/wishlist/WishlistPagination";
import { useWishlist } from "@/theme2/hooks/useWishlist";

const perPage = 8;

export default function Wishlist() {
  const {
    wishlistProducts,
    wishlistCount,
    removeFromWishlist,
    clearWishlist,
    moveToCart,
  } = useWishlist();

  const [sort, setSort] = useState("newest");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);

  const filteredProducts = useMemo(() => {
    let items = [...wishlistProducts];

    if (filter === "in-stock") {
      items = items.filter((item) => !item.isSoldOut);
    }

    if (filter === "sale") {
      items = items.filter((item) => item.discount);
    }

    if (filter === "best-seller") {
      items = items.filter((item) => item.isBestSeller);
    }

    if (filter === "new") {
      items = items.filter((item) => item.isNew);
    }

    items.sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sort === "alphabetical") return a.title.localeCompare(b.title);
      return b.id - a.id;
    });

    return items;
  }, [wishlistProducts, filter, sort]);

  const totalPages = Math.ceil(filteredProducts.length / perPage);
  const paginatedProducts = filteredProducts.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const totalValue = wishlistProducts.reduce(
    (sum, product) => sum + Number(product.price || 0),
    0
  );

  return (
    <>
      <Head title="Wishlist - Captex" />

      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="space-y-10">
          <WishlistHeader count={wishlistCount} />

          {wishlistCount > 0 ? (
            <>
              <WishlistSummary
                count={wishlistCount}
                totalValue={totalValue}
              />

              <WishlistToolbar
                sort={sort}
                onSortChange={setSort}
                filter={filter}
                onFilterChange={(value) => {
                  setFilter(value);
                  setPage(1);
                }}
                view={view}
                onViewChange={setView}
                onClear={clearWishlist}
                disabled={wishlistCount === 0}
              />

              <WishlistGrid
                products={paginatedProducts}
                view={view}
                onRemove={removeFromWishlist}
                onMoveToCart={moveToCart}
              />

              <WishlistPagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            </>
          ) : (
            <EmptyWishlist />
          )}
        </div>
      </div>
    </>
  );
}